// Single authoritative home for every IBAN / bank-account-number operation used
// across KSeF verification, reconcile, and Fakturownia sync. Never duplicate any
// of these — import from this module.

// ── Account separators ──────────────────────────────────────────────────────────

/**
 * Separator between multiple bank accounts joined into one raw field: the
 * literal "<BR>" Fakturownia sometimes emits (any case, optional self-closing
 * slash), or a run of newlines/semicolons/commas. Shared by `canonicalizeIbans`
 * (parses accounts out of a multi-account blob) and `formatIban` (formats each
 * account independently) so the two can never disagree about where one account
 * ends and the next begins.
 */
export const ACCOUNT_SEPARATOR_RE = /<br\s*\/?>|[\n\r;,]+/i;

// ── IBAN normalizer ────────────────────────────────────────────────────────────

/** Strip all whitespace from an IBAN/bank account number for comparison. */
export function normalizeIban(s: string | null | undefined): string {
  if (s == null) return '';
  return s.replace(/\s/g, '').toUpperCase();
}

/** Comparison key for bank accounts: whitespace-normalized AND with a leading
 * country code dropped. Fakturownia stores the full IBAN ("PL38 1050…") while the
 * KSeF XML <NrRB> is the bare national number ("38105…") — without dropping the
 * country code every clean invoice would false-positive as a mismatch. */
export function ibanCore(s: string | null | undefined): string {
  return normalizeIban(s).replace(/^[A-Z]{2}(?=\d)/, '');
}

/**
 * Bare Polish account number: whitespace stripped and a leading **PL** dropped —
 * every other country code is KEPT.
 *
 * Deliberately NOT `ibanCore`. Callers of this feed the MF white-list lookup, which
 * decides "is this Polish?" by whether the value starts with a digit. Strip a foreign
 * country code and a German IBAN becomes a digit-leading string that the lookup then
 * re-prefixes as "PL…" and sends to the tax-office API as garbage, instead of being
 * recognised as out-of-scope and skipped. Use `ibanCore` to COMPARE two accounts;
 * use this when the country code carries meaning downstream.
 */
export function toBarePolishAccount(raw: string | null | undefined): string {
  if (raw == null) return '';
  return raw.replace(/\s/g, '').replace(/^PL/i, '');
}

/**
 * THE storage format for `company_bank_accounts.account_number`. Strips all
 * whitespace and uppercases, then normalizes to a country-code-prefixed IBAN:
 *   - already prefixed ("PL..." / "XX1...") → returned as-is
 *   - bare 26-digit Polish NRB → "PL" prepended
 *   - anything else (garbage, unrecognized shape) → stripped/uppercased as-is
 *   - null/undefined/empty → null
 * Every writer of `account_number` must funnel through this so the column is
 * always in one canonical shape; matching still goes through `ibanCore` for
 * country-agnostic comparison.
 */
export function canonicalAccountNumber(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = raw.replace(/\s/g, '').toUpperCase();
  if (s === '') return null;
  if (/^[A-Z]{2}\d/.test(s)) return s;
  if (/^\d{26}$/.test(s)) return 'PL' + s;
  return s;
}

// ── NRB extractor ──────────────────────────────────────────────────────────────

/**
 * Extract a clean 26-digit Polish NRB from a raw KSeF <NrRB> string that may
 * carry IBAN prefixes, currency hints, bank suffixes, or decorative whitespace.
 *
 * Observed real-data formats (from live FA(3) XML survey):
 *   "IBANPL64808500050006433420000030"  → "64808500050006433420000030"
 *   "IBAN PL64 8085 0005 0006 4334 2000 0030" → "64808500050006433420000030"
 *   "EUR:PL73105011681000002301125080"  → "73105011681000002301125080"
 *   "07105011421000002347014603I"       → "07105011421000002347014603"
 *   "95105011391000002367288921INGBAN"  → "95105011391000002367288921"
 *   "PL38 1050 1214 1000 0090 3023 9256" → "38105012141000009030239256"
 *   "43102025280000010203794070"         → "43102025280000010203794070" (bare)
 *
 * Algorithm:
 *   1. Strip ALL non-digit characters.
 *   2. If the result is exactly 26 digits → return it (length is the hard rule).
 *   3. Otherwise → return null (unrecoverable; caller must not persist or use).
 *
 * Note: a leading '00' from a stripped 'PL' country code (P=25, L=21) becomes
 * digits that may inflate the count — we strip ALL non-digits which avoids any
 * letter-to-digit translation; letters simply vanish.
 *
 * Checksum (advisory only): we do NOT reject on a failed mod-97 checksum.
 * Real client data can have typos; a 26-digit string with a wrong checksum is still
 * recoverable (surfaces as a mismatch finding) whereas null is unreachable.
 *
 * Single definition — import from this module; never duplicate.
 */
export function extractNrrb(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 26) return null;
  return digits;
}

// ── Multi-account parsing ───────────────────────────────────────────────────────

/** Extract the set of canonical IBAN-like tokens from a (possibly multi-account, messy) string.
 *  Splits ONLY on account separators ("<BR>", newlines, ";", ",") — never on the spaces that
 *  format a single IBAN — then strips labels/prefixes/spacing within each account. */
export function canonicalizeIbans(value: unknown): Set<string> {
  if (value == null) return new Set();
  // Cast for no-base-to-string only; bank-account columns are text. Behavior unchanged.
  const raw = String(value as string | number);
  if (raw.trim() === '') return new Set();
  const out = new Set<string>();
  for (const seg of raw.split(ACCOUNT_SEPARATOR_RE)) {
    const c = seg
      .replace(/\biban\b/gi, '')     // "IBAN" label
      .replace(/[a-z]{3}\s*:/gi, '') // currency prefixes like "EUR:"
      .replace(/[^a-zA-Z0-9]/g, '')  // strip spaces + punctuation within the account
      .toUpperCase();
    if (c.length >= 15) out.add(c);  // account-number length floor
  }
  return out;
}

// ── Display formatting ──────────────────────────────────────────────────────────

/** Format one already-isolated account: strip whitespace, uppercase, prepend "PL"
 *  when no country code is present, regroup into 4-char blocks. Empty/whitespace-only
 *  input formats to ''. */
function formatSingleAccount(segment: string): string {
  const cleaned = segment.replace(/\s+/g, '').toUpperCase();
  if (!cleaned) return '';
  const withCountry = /^[A-Z]{2}/.test(cleaned) ? cleaned : `PL${cleaned}`;
  return withCountry.replace(/(.{4})/g, '$1 ').trim();
}

// Same account separator as ACCOUNT_SEPARATOR_RE, but widened to also swallow
// whitespace immediately touching the separator ("… 4895 <BR> 4310 …" → the
// captured separator is " <BR> ", not just "<BR>"). That whitespace is display
// padding around the separator, not part of either account, so re-attaching it
// to the separator (instead of leaving it for formatSingleAccount to strip) is
// what makes the rejoin preserve "…4895 <BR> PL43…" instead of "…4895<BR>PL43…".
const FORMAT_SPLIT_RE = new RegExp(`(\\s*(?:${ACCOUNT_SEPARATOR_RE.source})\\s*)`, ACCOUNT_SEPARATOR_RE.flags);

/**
 * Spaced display format. Multi-account aware: splits on the same account
 * separator `canonicalizeIbans` parses accounts with, applies the single-account
 * formatting logic to each non-empty segment, and rejoins on the original
 * separator text — so a "<BR>"-joined pair of accounts each gets its own
 * spacing/PL-prefix instead of the whole blob being regrouped as one string
 * (which walks straight through the separator and shifts the second account's
 * digits out of alignment). Never drops an account, even an empty one from a
 * leading/trailing/doubled separator.
 *
 * A single-account input has no separator to split on, so it round-trips
 * through `formatSingleAccount` byte-identical to today's single-account
 * `CompanyValidator.formatIban` output.
 */
export function formatIban(account: string | null | undefined): string | null {
  if (!account) return null;
  if (!account.trim()) return null;
  const parts = account.split(FORMAT_SPLIT_RE);
  const result = parts
    .map((part, i) => (i % 2 === 0 ? formatSingleAccount(part) : part))
    .join('');
  return result || null;
}

// ── Shape validation ──────────────────────────────────────────────────────────

/**
 * Canonicalize (via `canonicalAccountNumber`) then shape-gate: returns the canonical
 * form when it looks like an IBAN (`[A-Z]{2}\d{2}[A-Z0-9]{11,30}`), otherwise null.
 * Used by payment-matching call sites that need a strict valid-or-null result rather
 * than `canonicalAccountNumber`'s permissive garbage-passthrough.
 *
 * Deliberately does NOT strip a leading slash — the SWIFT `:25:` `/`-prefix quirk is
 * caller-specific (see `matching/paymentMatcher.ts`); stripping it here would make
 * every caller accept inputs that today return null.
 */
export function toValidIban(raw: string | null | undefined): string | null {
  const canonical = canonicalAccountNumber(raw);
  if (canonical == null) return null;
  return /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(canonical) ? canonical : null;
}

/**
 * Canonical IBAN for a SWIFT-sourced value, or null when it is not IBAN-shaped.
 *
 * mBank's MT940 `:25:` and camt `Othr/Id` fields prefix the account with `/` (the
 * SWIFT "account with bank" subfield). Whitespace is stripped BEFORE the slash so a
 * padded value (" /PL12…") still has its slash recognised.
 */
export function toValidSwiftIban(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return toValidIban(raw.replace(/\s+/g, '').replace(/^\/+/, ''));
}

// ── Checksum validation ─────────────────────────────────────────────────────────

/** Strip whitespace/uppercase; prepend "PL" for bare-digit input. Reports whether
 *  the account looks Polish (bare digits or already "PL"-prefixed). */
export function cleanAccount(raw: string): { cleaned: string; isPolish: boolean } {
  let cleaned = raw.replace(/\s/g, '').toUpperCase();
  const isPolish = /^\d/.test(cleaned) || cleaned.startsWith('PL');
  if (/^\d/.test(cleaned)) cleaned = 'PL' + cleaned;
  return { cleaned, isPolish };
}

/** Mod-97 IBAN checksum (ISO 7064). */
export function checkIbanChecksum(iban: string): boolean {
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numericStr = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (let i = 0; i < numericStr.length; i += 7) {
    const chunk = String(remainder) + numericStr.slice(i, i + 7);
    remainder = parseInt(chunk, 10) % 97;
  }
  return remainder === 1;
}

/** Full IBAN validity check: format normalization + mod-97 checksum. */
export function isValidIban(raw: string): boolean {
  if (!raw) return false;
  const { cleaned } = cleanAccount(raw);
  return checkIbanChecksum(cleaned);
}
